import * as React from "react";

import {
    LeftSideMenu,
    MenuItemSpec,
} from "@impero-com/common/components/leftSideMenu";
import { BreadcrumbSpec } from "@impero-com/common/componentTypes/breadcrumb";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import createIcon from "@impero-com/common/img/create-icon.svg";
import { t } from "@impero-com/common/utils/i18n";
import {
    ActionMenu,
    ActionMenuProps,
    actionMenuStatus,
    ApplicationBarToolbar,
    PageColumns,
    PageContent,
    PageLayout,
    PageNavMenu,
    PageNavMenuItem,
    PageNavMenuSection,
    PageSpinner,
} from "@impero-com/ui";

import {
    AvailableActions,
    ControlControlsResourceCount,
    ControlResourceCount,
} from "api/generated/model";
import shareIcon from "img/share-icon.svg";

import { OrgApplicationBar } from "../../../components/applicationBar/orgApplicationBar";
import { OrgApplicationBarHeader } from "../../../components/applicationBar/OrgApplicationBarHeader";
import { PageNavMenuLayout, PageNavMenuMain } from "../../../components/layout";
import { WebRoute } from "../../../routes";
import { useNewMenu } from "../../navigation/useNewMenu";

import { useControlsResourceCountQuery } from "./controls/queries/useControlsResourceCountQuery";

const PAGE_CONTROL_PROGRAMS = "CONTROL_PROGRAMS";
const MENU_CONTROLS = "CONTROLS";
const PAGE_CONTROLS_ALL = "CONTROLS_ALL";
const PAGE_CONTROLS_WEEKLY = "CONTROLS_WEEKLY";
const PAGE_CONTROLS_MONTHLY = "CONTROLS_MONTHLY";
const PAGE_CONTROLS_TWO_MONTHS = "CONTROLS_TWO_MONTHS";
const PAGE_CONTROLS_QUARTERLY = "CONTROLS_QUARTERLY";
const PAGE_CONTROLS_HALF_YEARLY = "CONTROLS_HALF_YEARLY";
const PAGE_CONTROLS_YEARLY = "CONTROLS_YEARLY";
const PAGE_CONTROLS_MANUAL = "CONTROLS_MANUAL";

type ControlModulePage =
    | typeof PAGE_CONTROL_PROGRAMS
    | typeof PAGE_CONTROLS_ALL
    | typeof PAGE_CONTROLS_MONTHLY
    | typeof PAGE_CONTROLS_TWO_MONTHS
    | typeof PAGE_CONTROLS_QUARTERLY
    | typeof PAGE_CONTROLS_HALF_YEARLY
    | typeof PAGE_CONTROLS_YEARLY
    | typeof PAGE_CONTROLS_MANUAL
    | typeof PAGE_CONTROLS_WEEKLY;

type ControlViewPage = Exclude<ControlModulePage, typeof PAGE_CONTROL_PROGRAMS>;

interface ControlModuleApplicationBarProps {
    page: ControlModulePage;
    title: string;
    breadcrumbs: Array<{ label: string; url: string }>;
    availableActions: AvailableActions;
    onCreateShareableLink?: () => void;
    children: React.ReactNode;
}

/**
 * Renders no page navigation menu on the new menu. The control views use
 * `ControlViewsLayout` and `ControlViewsMain` there instead.
 */
function ControlModuleApplicationBar({
    page,
    title,
    breadcrumbs,
    availableActions,
    onCreateShareableLink,
    children,
}: ControlModuleApplicationBarProps) {
    const newMenu = useNewMenu();
    const header = controlModuleHeader(title, breadcrumbs);
    const actionMenu = controlModuleActionMenu(
        availableActions,
        onCreateShareableLink
    );

    if (newMenu) {
        return (
            <OrgApplicationBar title={header} actions={actionMenu}>
                {children}
            </OrgApplicationBar>
        );
    }

    return (
        <ControlModuleMenuLayout
            page={page}
            title={title}
            header={header}
            actionMenu={actionMenu}
        >
            {children}
        </ControlModuleMenuLayout>
    );
}

interface ControlModuleMenuLayoutProps {
    page: ControlModulePage;
    title: string;
    header: BreadcrumbSpec;
    actionMenu: ActionMenuProps;
    children: React.ReactNode;
}

function ControlModuleMenuLayout({
    page,
    title,
    header,
    actionMenu,
    children,
}: ControlModuleMenuLayoutProps) {
    const { data, error } = useControlsResourceCountQuery();

    if (data) {
        return (
            <PageLayout fullHeight>
                <OrgApplicationBarHeader title={header}>
                    <ApplicationBarToolbar
                        actions={<ActionMenu {...actionMenu} />}
                    />
                </OrgApplicationBarHeader>
                <PageColumns>
                    <LeftSideMenu
                        items={menuItems(data, controlViewRows(page))}
                        selectedItemId={page}
                        onItemSelected={() => {}}
                    />
                    <PageContent>{children}</PageContent>
                </PageColumns>
            </PageLayout>
        );
    }

    if (error) {
        return (
            <OrgApplicationBar title={title}>
                <LoggedInHTTPError code={error.response?.status} />
            </OrgApplicationBar>
        );
    }

    return <PageSpinner />;
}

interface ControlViewsLayoutProps {
    page: ControlViewPage;
    title: string;
    breadcrumbs: Array<{ label: string; url: string }>;
    /**
     * The `ControlViewsMain`.
     */
    children: React.ReactNode;
}

function ControlViewsLayout({
    page,
    title,
    breadcrumbs,
    children,
}: ControlViewsLayoutProps) {
    return (
        <PageNavMenuLayout
            title={controlModuleHeader(title, breadcrumbs)}
            menu={<ControlViewsMenu page={page} />}
        >
            {children}
        </PageNavMenuLayout>
    );
}

interface ControlViewsMainProps {
    availableActions: AvailableActions;
    onCreateShareableLink?: () => void;
    children: React.ReactNode;
}

function ControlViewsMain({
    availableActions,
    onCreateShareableLink,
    children,
}: ControlViewsMainProps) {
    return (
        <PageNavMenuMain
            actions={
                <ActionMenu
                    {...controlModuleActionMenu(
                        availableActions,
                        onCreateShareableLink
                    )}
                />
            }
        >
            {children}
        </PageNavMenuMain>
    );
}

interface ControlViewsMenuProps {
    page: ControlViewPage;
}

function ControlViewsMenu({ page }: ControlViewsMenuProps) {
    const { data } = useControlsResourceCountQuery();
    const controls = data?.controls;

    return (
        <PageNavMenu aria-label={t("RiskMapResponse_Columns_Controls")}>
            <PageNavMenuSection label={t("RiskMapResponse_Columns_Controls")}>
                {controlViewRows(page).map(
                    ({ id, label, to, count, active }) => {
                        const total = controls && count(controls);
                        return (
                            <PageNavMenuItem
                                key={id}
                                label={label}
                                to={to}
                                count={total === 0 ? undefined : total}
                                active={active}
                            />
                        );
                    }
                )}
            </PageNavMenuSection>
        </PageNavMenu>
    );
}

function controlModuleHeader(
    title: string,
    breadcrumbs: Array<{ label: string; url: string }>
): BreadcrumbSpec {
    return {
        title,
        breadCrumbs: [
            {
                label: t("ControlManagement_ModuleTitle"),
                url: WebRoute.CONTROLS,
            },
            ...breadcrumbs,
        ],
    };
}

function controlModuleActionMenu(
    availableActions: AvailableActions,
    onCreateShareableLink: (() => void) | undefined
): ActionMenuProps {
    const shareableLink = onCreateShareableLink
        ? [
              {
                  id: "shareable-link-create-new",
                  label: t("ShareableLink_CreateNew"),
                  icon: { svg: shareIcon },
                  onClick: onCreateShareableLink,
              },
          ]
        : [];

    return {
        id: "action-menu-controls",
        actions: [
            ...shareableLink,
            {
                id: "cp-create-new",
                label: t("ControlProgram_CreateNew"),
                icon: { svg: createIcon },
                link: { href: WebRoute.CONTROL_PROGRAMS_NEW },
                status: actionMenuStatus(
                    availableActions.controlProgram.create,
                    t("ControlProgram_Create_Forbidden")
                ),
            },
            {
                id: "c-create-new",
                label: t("Dashboard_ControlProgram_CreateNewControl"),
                icon: { svg: createIcon },
                link: { to: WebRoute.CONTROLS_NEW },
                status: actionMenuStatus(
                    availableActions.control.create,
                    t("Control_Create_Forbidden")
                ),
            },
        ],
    };
}

interface ControlViewRow {
    id: ControlViewPage;
    label: string;
    to: string;
    count: (controls: ControlControlsResourceCount) => number;
    active: boolean;
}

function controlViewRows(page: ControlModulePage): Array<ControlViewRow> {
    const views: Array<Omit<ControlViewRow, "active">> = [
        {
            id: PAGE_CONTROLS_ALL,
            label: t("Controls_All"),
            to: WebRoute.CONTROLS_ALL,
            count: (controls) => controls.all,
        },
        {
            id: PAGE_CONTROLS_WEEKLY,
            label: t("CreateControl_Recurrence_Interval_Weekly"),
            to: WebRoute.CONTROLS_WEEKLY,
            count: (controls) => controls.weekly,
        },
        {
            id: PAGE_CONTROLS_MONTHLY,
            label: t("CreateControl_Recurrence_Interval_Monthly"),
            to: WebRoute.CONTROLS_MONTHLY,
            count: (controls) => controls.monthly,
        },
        {
            id: PAGE_CONTROLS_TWO_MONTHS,
            label: t("CreateControl_Recurrence_Interval_Every2Months"),
            to: WebRoute.CONTROLS_TWO_MONTHS,
            count: (controls) => controls.everyTwoMonths,
        },
        {
            id: PAGE_CONTROLS_QUARTERLY,
            label: t("CreateControl_Recurrence_Interval_Quarterly"),
            to: WebRoute.CONTROLS_QUARTERLY,
            count: (controls) => controls.quarterly,
        },
        {
            id: PAGE_CONTROLS_HALF_YEARLY,
            label: t("CreateControl_Recurrence_Interval_HalfYearly"),
            to: WebRoute.CONTROLS_HALF_YEARLY,
            count: (controls) => controls.halfYearly,
        },
        {
            id: PAGE_CONTROLS_YEARLY,
            label: t("CreateControl_Recurrence_Interval_Yearly"),
            to: WebRoute.CONTROLS_YEARLY,
            count: (controls) => controls.yearly,
        },
        {
            id: PAGE_CONTROLS_MANUAL,
            label: t("CreateControl_Recurrence_Manual"),
            to: WebRoute.CONTROLS_MANUAL,
            count: (controls) => controls.manual,
        },
    ];
    return views.map((view) => ({ ...view, active: view.id === page }));
}

function menuItems(
    { controlPrograms, controls }: ControlResourceCount,
    controlViews: Array<ControlViewRow>
): Array<MenuItemSpec> {
    return [
        {
            type: "SimpleMenuItem",
            id: PAGE_CONTROL_PROGRAMS,
            label: `${t("ControlPrograms")} (${controlPrograms})`,
            handler: { type: "ROUTER", linkTo: WebRoute.CONTROLS },
        },
        {
            type: "SubMenuItem",
            id: MENU_CONTROLS,
            label: t("RiskMapResponse_Columns_Controls"),
            childItems: controlViews.map(
                ({ id, label, to, count }): MenuItemSpec => ({
                    type: "SimpleMenuItem",
                    id,
                    label: `${label} (${count(controls)})`,
                    handler: { type: "ROUTER", linkTo: to },
                })
            ),
        },
    ];
}

export {
    ControlModuleApplicationBar,
    ControlViewsLayout,
    ControlViewsMain,
    PAGE_CONTROL_PROGRAMS,
    MENU_CONTROLS,
    PAGE_CONTROLS_ALL,
    PAGE_CONTROLS_WEEKLY,
    PAGE_CONTROLS_MONTHLY,
    PAGE_CONTROLS_TWO_MONTHS,
    PAGE_CONTROLS_QUARTERLY,
    PAGE_CONTROLS_HALF_YEARLY,
    PAGE_CONTROLS_YEARLY,
    PAGE_CONTROLS_MANUAL,
    type ControlModulePage,
    type ControlViewPage,
};
